import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { StainInput } from 'components/StudyDashboard/ProceduresPage/Actions/utils';
import { map, size, uniq } from 'lodash';
import React, { useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { RunSlideRegistrationsFormValues } from './types';
export type SlideMetadataSide = 'sourceFieldName' | 'targetFieldName';

export interface SlideMetadataOptionConfig {
  value: string;
  text: string;
  sourceFieldName: string;
  targetFieldName: string;
  options: DropdownOption[];
}

export type SlideMetadataOptionsByKey<Key extends string = string> = Record<Key, SlideMetadataOptionConfig>;

export interface SlideMetadataProps<MetadataOptionKey extends string = string> {
  id: string;
  title: string;
  metadataOptionFieldName: SlideMetadataSide;

  stainFieldName: string;
  stainOptions: DropdownOption[];

  control: Control<RunSlideRegistrationsFormValues, any>;
  errors: FieldErrors<RunSlideRegistrationsFormValues>;

  metadataOptions: SlideMetadataOptionsByKey<MetadataOptionKey>;
  selectedMetadataOptions: MetadataOptionKey[];
  setSelectedMetadataOptions: React.Dispatch<React.SetStateAction<MetadataOptionKey[]>>;

  availableMetadataOptionKeys: MetadataOptionKey[];

  loading: boolean;

  onRemoveMetadataOption: (optionKey: MetadataOptionKey) => void;
  onValidate: () => void;

  createSlideManifest: () => void;
}

export const SlideMetadata = <MetadataOptionKey extends string = string>({
  id,
  title,
  metadataOptionFieldName,
  stainFieldName,
  stainOptions,
  control,
  errors,
  metadataOptions,
  selectedMetadataOptions,
  setSelectedMetadataOptions,
  availableMetadataOptionKeys,
  loading,
  onRemoveMetadataOption,
  onValidate,
  createSlideManifest,
}: SlideMetadataProps<MetadataOptionKey>) => {
  const [addAnchorEl, setAddAnchorEl] = useState<HTMLElement | null>(null);
  const cyBase = `run-slide-registrations-${id}`;

  return (
    <Grid item data-cy={`${cyBase}-section`}>
      <Typography mb={1} variant="body1">
        {title}
      </Typography>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={3}>
          <StainInput
            key={stainFieldName}
            label="Stain"
            name={stainFieldName}
            options={stainOptions}
            control={control}
            errors={errors}
            loading={loading}
            dataTestId={`${cyBase}-${stainFieldName}-select`}
            onSelect={() => {
              onValidate();
              createSlideManifest();
            }}
          />
        </Grid>

        {map(selectedMetadataOptions, (optionKey) => {
          return (
            <Grid item xs={3} key={optionKey}>
              <Box sx={{ position: 'relative' }}>
                <Controller
                  control={control}
                  name={metadataOptions[optionKey][metadataOptionFieldName] as keyof RunSlideRegistrationsFormValues}
                  render={({ field: { onChange, value } }) => (
                    <LabelledDropdown
                      label={metadataOptions[optionKey].text}
                      options={metadataOptions[optionKey].options}
                      value={value ?? ''}
                      dataTestId={`${cyBase}-metadata-${String(optionKey)}-select`}
                      onOptionSelected={(optionValue) => {
                        onChange(optionValue);
                        onValidate();
                        createSlideManifest();
                      }}
                    />
                  )}
                />
                <Tooltip title={`Remove ${metadataOptions[optionKey].text}`}>
                  <IconButton
                    aria-label={`Remove ${metadataOptions[optionKey].text}`}
                    data-cy={`${cyBase}-metadata-${String(optionKey)}-remove`}
                    onClick={() => {
                      onRemoveMetadataOption(optionKey);
                      createSlideManifest();
                    }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: 6,
                      transform: 'translateY(-50%)',
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'background.default' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          );
        })}

        <Grid item xs={3} mt={3}>
          <Button
            variant="outlined"
            disabled={loading || size(availableMetadataOptionKeys) === 0}
            data-cy={`${cyBase}-add-metadata-option`}
            onClick={(event) => setAddAnchorEl(event.currentTarget)}
          >
            Add metadata option
          </Button>
          <Menu anchorEl={addAnchorEl} open={Boolean(addAnchorEl)} onClose={() => setAddAnchorEl(null)}>
            {map(availableMetadataOptionKeys, (optionKey) => (
              <MenuItem
                key={optionKey}
                data-cy={`${cyBase}-add-metadata-option-${String(optionKey)}`}
                onClick={() => {
                  setSelectedMetadataOptions((prev) => uniq([...prev, optionKey] as MetadataOptionKey[]));
                  setAddAnchorEl(null);
                  onValidate();
                }}
              >
                {metadataOptions[optionKey].text}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
      </Grid>
    </Grid>
  );
};
